import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
// import '@adornis/buchungsheld-theme/components/index.js';
import '@adornis/buchungsheld-theme/components/tr-button.js';
import '@adornis/buchungsheld-theme/components/tr-formfield-wrapper.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import '@adornis/buchungsheld-theme/components/tr-input.js';
import '@adornis/buchungsheld-theme/fonts/index.js';
import '@adornis/buchungsheld-theme/theme.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import '@adornis/chemistry/elements/components/x-link.js';
import '@adornis/chemistry/elements/components/x-section.js';
import '@adornis/popover/x-dropdown.js';
import type { XDropdown } from '@adornis/popover/x-dropdown.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { Language } from '@adornis/translation-core/languages.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import logo from '../assets/logo.jpeg';

import '@adornis/buchungsheld-theme/index.js';

/**
 * @element bh-topbar-copy
 */
@customElement('bh-topbar-copy')
export class BHTopbarCopy extends BuchungsheldLitElement {
  override styles(): ReturnType<BuchungsheldLitElement['styles']> {
    return [
      ...super.styles(),
      {
        ':host': {
          backdropFilter: 'blur(4px) grayscale(1)',
          background: 'rgb(255 255 255 / 96%)',
          boxShadow: `${this.colors.black.opacity(0.1)} 4px 4px 40px`,
          position: 'sticky',
          top: '0px',
          zIndex: '999999',
        },
      },
    ];
  }
  @property() page?: string;

  @state() private readonly _currentUser = new RXController(this, currentUser<User>());
  @state() private readonly _language = new RXController(this, globalLanguageSubject);

  override render() {
    const items = (() => {
      if (this.clientWidth > 500) return [this._loginLogout(), this._home()];

      const dropdown_ref = createRef<XDropdown>();
      return html`
        <tr-button mode="icon" icon="menu" @click=${() => dropdown_ref.value?.toggle()}>
          ${this._translation.translate('filter')}
        </tr-button>
        <x-dropdown ${ref(dropdown_ref)}>
          <x-flex slot="dropdown-content">
            <x-card>
              <x-flex vertical crossaxis-start ${css({ gap: '14px' })}>
                ${[this._home(true), this._loginLogout()]}
              </x-flex>
            </x-card>
          </x-flex>
        </x-dropdown>
      `;
    })();

    return html`
      <x-section>
        <x-flex horizontal end crossaxis-center space="sm" ${css({ padding: `${this.spacing.md} 0` })}>
          ${this._logo()} ${this._languageSwitch()} ${items}
        </x-flex>
      </x-section>
    `;
  }

  private _languageSwitch() {
    return html`
      <x-button
        mode="text"
        ${css({ textTransform: 'uppercase' })}
        @click=${() =>
          globalLanguageSubject.next(this._language.value === Language.GERMAN ? Language.ENGLISH : Language.GERMAN)}
      >
        ${this._language.value}
      </x-button>
    `;
  }

  private _loginLogout() {
    return this._currentUser.isLoading
      ? html` <tr-text> ... </tr-text>`
      : this._currentUser.value
        ? html`
            <tr-button mode="text" icon="logout" @click=${() => logout()}>
              ${this._translation.translate('LOGOUT')}
            </tr-button>
          `
        : html`
            <tr-button mode="text" icon="login" href="/profile"> ${this._translation.translate('LOGIN')} </tr-button>
          `;
  }

  private _home(withText?: boolean) {
    const text = withText ? this._translation.translate('home') : '';
    return html` <tr-button mode="text" icon="home" href="/">${text}</tr-button> `;
  }

  private _logo() {
    if (this.isInIframe()) return nothing;

    return html`
      <x-link href="/" ${css({ 'margin-right': 'auto' })}>
        <img src="${logo}" ${css({ height: '3em', marginTop: `-${this.spacing.xs}` })} />
      </x-link>
    `;
  }

  private isInIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }
}
